import { useState, useEffect, useRef, useCallback } from "react";
import {
  ChatJessMessage,
  ChatJessConnectionStatus,
  ChatJessState,
  ChatItem,
  ChatItemType,
  Message,
  HumanRequestInfo,
  REQUEST_TYPES,
  RESPONSE_TYPES,
  ChatConnectedMessage,
  ChatConnectedPayload,
  TeamMessage,
  WebSocketMessage,
  HumanSupportDTO,
  HumanSupport,
  HumanSupportMessage,
  HumanQAAnswerMessage,
  FileAttachment,
} from "../../pages/Dashboard/ChatJess/types";
import {
  connectToChatJessWebSocket,
  disconnectChatJessWebSocket,
} from "../../services/chatJess/connectToChatJessWebSocket";
import { logger } from "../../utils/logger";
import {
  WorkflowEventType,
  type HumanSupportType,
  type Task,
  type HumanSupport as TaskHumanSupport,
} from "../../types/tasks";
import { useNotificationSound } from "../useNotificationSound";

interface UseChatJessWebSocketProps {
  initialMessages?: ChatJessMessage[];
  onNewMessage?: (message: ChatJessMessage) => void;
  onFeedbackRequest?: (message: ChatJessMessage) => void;
  onError?: (error: Error) => void;
  onChatHistoryReceived?: () => void;
  onChatConnectedSent?: () => void;
  onKpiDashboardUpdate?: (payload: any) => void;
}

export interface UseChatJessWebSocketReturn {
  // Estado
  messages: ChatJessMessage[];
  isTyping: boolean;
  connection: ChatJessConnectionStatus;
  isWaitingForResponse: boolean;

  // Ações
  sendMessage: (message: ChatJessMessage) => void;
  sendFeedbackResponse: (
    feedbackId: string,
    response: string,
    requestMessage?: ChatJessMessage,
    attachments?: FileAttachment[],
    questionIndex?: number,
  ) => void;
  sendTaskFeedback: (task: Task, stepId: string, feedback: string) => void;
  sendChatConnected: (clientID: string, clientType: "staff" | "user") => void;
  reconnect: () => void;
  disconnect: () => void;

  // Utilitários
  isConnected: boolean;
}

export const useChatJessWebSocket = ({
  initialMessages = [],
  onNewMessage,
  onFeedbackRequest,
  onError,
  onChatHistoryReceived,
  onChatConnectedSent,
  onKpiDashboardUpdate,
}: UseChatJessWebSocketProps = {}): UseChatJessWebSocketReturn => {
  const [state, setState] = useState<ChatJessState>({
    messages: initialMessages,
    isTyping: false,
    connection: {
      isConnected: false,
      isConnecting: false,
      error: null,
    },
    selectedFeedback: null,
    isWaitingForResponse: false,
  });

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;
  const historyLoadedRef = useRef(false);
  const connectedClientIDRef = useRef<string>("");
  const waitingForHistoryRef = useRef(false);
  const historyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sound notification hook
  const { playSound } = useNotificationSound();

  // Helper function to generate unique message IDs
  const generateUniqueId = useCallback(
    (originalId: string, suffix: string = ""): string => {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substr(2, 9);
      return `${originalId}${suffix ? `-${suffix}` : ""}-${timestamp}-${random}`;
    },
    [],
  );

  // Handler para mensagens do WebSocket
  const handleWebSocketMessage = useCallback(
    (event: any) => {
      switch (event.type) {
        case "message":
          if (event.data.message) {
            setState((prev) => {
              const newMessage = { ...event.data.message! };

              // Check if message with this ID already exists
              const existingMessage = prev.messages.find(
                (msg) => msg.id === newMessage.id,
              );

              if (existingMessage) {
                // Generate random ID to avoid duplicate keys
                newMessage.id = generateUniqueId(newMessage.id, "msg");
              }

              return {
                ...prev,
                messages: [...prev.messages, newMessage],
                isTyping: false,
                isWaitingForResponse: false,
              };
            });
            onNewMessage?.(event.data.message);

            // Play notification sound for new messages (not history)
            if (!waitingForHistoryRef.current && historyLoadedRef.current) {
              playSound();
            }

            // If we're waiting for history and receive a message, consider history loaded
            if (waitingForHistoryRef.current) {
              // Set a short timeout to collect multiple messages that might come in quick succession
              if (historyTimeoutRef.current) {
                clearTimeout(historyTimeoutRef.current);
              }
              historyTimeoutRef.current = setTimeout(() => {
                waitingForHistoryRef.current = false;
                historyLoadedRef.current = true;
                onChatHistoryReceived?.();
              }, 500); // Wait 500ms to collect any additional messages
            }
          }
          break;

        case "feedback_request":
          if (event.data.message) {
            setState((prev) => {
              const newMessage = { ...event.data.message! };

              // Check if a message with the same feedbackId already exists
              const existingMessageIndex = prev.messages.findIndex(
                (msg) =>
                  msg.feedbackId === newMessage.feedbackId &&
                  newMessage.feedbackId,
              );

              if (existingMessageIndex !== -1) {
                // Update existing message with new data (especially supportMessages)
                const updatedMessages = [...prev.messages];
                updatedMessages[existingMessageIndex] = {
                  ...updatedMessages[existingMessageIndex],
                  ...newMessage,
                  // Preserve the original ID to maintain React keys
                  id: updatedMessages[existingMessageIndex].id,
                };

                return {
                  ...prev,
                  messages: updatedMessages,
                  isWaitingForResponse: false,
                };
              }

              // If no existing message found, check by ID as fallback
              const existingMessageByIdIndex = prev.messages.findIndex(
                (msg) => msg.id === newMessage.id,
              );

              if (existingMessageByIdIndex !== -1) {
                // Generate random ID to avoid duplicate keys
                newMessage.id = generateUniqueId(newMessage.id, "feedback");
              }

              // Add as new message
              return {
                ...prev,
                messages: [...prev.messages, newMessage],
                isWaitingForResponse: false,
              };
            });
            onFeedbackRequest?.(event.data.message);

            // Play notification sound for feedback requests (question threads)
            if (!waitingForHistoryRef.current && historyLoadedRef.current) {
              playSound();
            }
          }
          break;

        case "typing":
          setState((prev) => ({
            ...prev,
            isTyping: event.data.isTyping || false,
          }));

          // Auto-clear typing após duração especificada
          if (event.data.isTyping && event.data.duration) {
            setTimeout(() => {
              setState((prev) => ({ ...prev, isTyping: false }));
            }, event.data.duration);
          }
          break;

        case "connection":
          break;

        case "error": {
          const error = new Error(
            event.data.error || "Erro desconhecido do WebSocket",
          );
          logger.error("[ChatJess Hook] Erro recebido:", error);
          onError?.(error);
          setState((prev) => ({
            ...prev,
            connection: {
              ...prev.connection,
              error: error.message,
            },
          }));
          break;
        }

        case RESPONSE_TYPES.CHAT_HISTORY:
          if (event.data.items && Array.isArray(event.data.items)) {
            const historyMessages: ChatJessMessage[] = [];

            event.data.items.forEach((item: ChatItem) => {
              if (item.type === ChatItemType.Message) {
                const msg = item.payload as Message;

                // Process media attachments if present
                let attachments: FileAttachment[] = [];
                let audioData: any = undefined;
                let isAudioMessage = false;
                let messageType: "text" | "file" | "mixed" | "audio" =
                  msg.hasMedia ? "file" : "text";

                if (msg.hasMedia && msg.media) {
                  // Create attachment from the single media object
                  const attachment: FileAttachment = {
                    id: `${item.id}-media-0`,
                    name: msg.media.filename || `attachment`,
                    size: msg.media.filesize || 0,
                    type: msg.media.mimetype || "application/octet-stream",
                    url: msg.media.url,
                  };

                  // For images, set thumbnail to the same URL for better display
                  if (
                    msg.media.mimetype &&
                    msg.media.mimetype.startsWith("image/")
                  ) {
                    attachment.thumbnail = msg.media.url;
                  }

                  attachments = [attachment];

                  // Check if this is an audio message
                  if (
                    msg.media.mimetype &&
                    msg.media.mimetype.startsWith("audio/")
                  ) {
                    isAudioMessage = true;
                    messageType = "audio";

                    // Create audioData for proper audio playback
                    audioData = {
                      url: msg.media.url,
                      blob: undefined, // Not available from history
                      base64: "", // Not needed for URL-based audio
                      duration: 0, // Duration not available from history
                      mimeType: msg.media.mimetype,
                      waveformData: undefined, // Not available from history
                    };
                  }
                }

                // Ensure attachments are properly formed
                const finalAttachments =
                  attachments.length > 0 ? attachments : undefined;

                // Create the message object
                const historyMessage: ChatJessMessage = {
                  id: item.id, // Use ChatItem.id instead of msg.id
                  content: msg.body || "",
                  author: msg.fromMe ? "jess" : "human",
                  timestamp: new Date(msg.timestamp * 1000),
                  messageType,
                  attachments: finalAttachments,
                  audioData,
                  isAudioMessage,
                };

                historyMessages.push(historyMessage);
              } else if (item.type === ChatItemType.HumanRequest) {
                const request = item.payload as HumanRequestInfo;

                // Build conversation flow from team_messages
                // Assistant messages (who: "assistant") are questions
                // User messages (who: "user") are answers
                const conversationFlow: {
                  question: string;
                  answer?: string;
                  media?: any;
                  answerMedia?: any[];
                }[] = [];

                // Sort team_messages by timestamp to ensure correct order (ISO strings)
                const sortedMessages = [...request.team_messages].sort(
                  (a, b) =>
                    new Date(a.timestamp).getTime() -
                    new Date(b.timestamp).getTime(),
                );

                let currentQuestion: string | null = null;
                let currentMedia: any = null;

                for (const teamMsg of sortedMessages) {
                  if (teamMsg.who === "assistant") {
                    // Assistant message - this is a question
                    if (currentQuestion !== null) {
                      // Previous question had no answer, add it
                      conversationFlow.push({
                        question: currentQuestion,
                        answer: undefined,
                        media: currentMedia,
                      });
                    }
                    currentQuestion = teamMsg.body || teamMsg.message;
                    // Handle media - backend sends media as array
                    const mediaArr = Array.isArray(teamMsg.media)
                      ? teamMsg.media
                      : teamMsg.media
                        ? [teamMsg.media]
                        : [];
                    currentMedia =
                      mediaArr.length > 0
                        ? {
                            url: mediaArr[0].url,
                            mimetype:
                              mediaArr[0].mime_type || mediaArr[0].mimetype,
                            filename:
                              mediaArr[0].filename ||
                              (mediaArr[0].metadata === "file"
                                ? mediaArr[0].url?.split("/").pop()
                                : undefined),
                          }
                        : null;
                  } else if (teamMsg.who === "user") {
                    // User message - this is an answer to the current question
                    if (currentQuestion !== null) {
                      const userMedia =
                        teamMsg.media &&
                        Array.isArray(teamMsg.media) &&
                        teamMsg.media.length > 0
                          ? teamMsg.media.map((m: any) => ({
                              url: m.url,
                              mimetype: m.mime_type || m.mimetype,
                              filename: m.filename,
                              filesize: m.filesize,
                            }))
                          : undefined;

                      conversationFlow.push({
                        question: currentQuestion,
                        answer: teamMsg.body || teamMsg.message,
                        media: currentMedia,
                        answerMedia: userMedia,
                      });
                      currentQuestion = null;
                      currentMedia = null;
                    }
                  }
                }

                // If there's a remaining question without answer, add it
                if (currentQuestion !== null) {
                  conversationFlow.push({
                    question: currentQuestion,
                    answer: undefined,
                    media: currentMedia,
                  });
                }

                historyMessages.push({
                  id: item.id, // Use ChatItem.id
                  content: request.message,
                  author: "jess",
                  timestamp: new Date(request.created_at),
                  feedbackId: `${request.human_qa_id}`,
                  hasFeedback: true,
                  supportMessages: conversationFlow,
                  messageType: "text",
                  supportType: request.humanSupportType,
                  isCompleted: request.isCompleted,
                  stepId: request.stepId,
                  // Store original team_messages for reference when submitting answers
                  originalTeamMessages: request.team_messages,
                });
              }
            });

            // Prepend history messages to existing messages, avoiding duplicates
            setState((prev) => {
              const existingIds = new Set(prev.messages.map((msg) => msg.id));
              const uniqueHistoryMessages = historyMessages.filter(
                (historyMsg) => {
                  if (existingIds.has(historyMsg.id)) {
                    // Generate new ID for duplicate to avoid React key conflicts
                    historyMsg.id = generateUniqueId(historyMsg.id, "history");
                  }
                  return true;
                },
              );

              return {
                ...prev,
                messages: [...uniqueHistoryMessages, ...prev.messages],
              };
            });

            // Reset waiting flag and notify that chat history was received
            waitingForHistoryRef.current = false;
            historyLoadedRef.current = true;
            if (historyTimeoutRef.current) {
              clearTimeout(historyTimeoutRef.current);
              historyTimeoutRef.current = null;
            }
            onChatHistoryReceived?.();
          }
          break;

        case RESPONSE_TYPES.REQUEST_HUMAN_SUPPORT:
        case RESPONSE_TYPES.HUMAN_QA_TEAM_MESSAGE_UPDATE:
          if (event.data.message) {
            setState((prev) => {
              const newMessage = { ...event.data.message! };

              // Find the existing message by feedbackId
              const existingMessageIndex = prev.messages.findIndex(
                (msg) =>
                  msg.feedbackId === newMessage.feedbackId &&
                  newMessage.feedbackId,
              );

              if (existingMessageIndex !== -1) {
                // Update existing message with new supportMessages
                const updatedMessages = [...prev.messages];
                updatedMessages[existingMessageIndex] = {
                  ...updatedMessages[existingMessageIndex],
                  ...newMessage,
                  // Preserve the original ID to maintain React keys
                  id: updatedMessages[existingMessageIndex].id,
                };

                return {
                  ...prev,
                  messages: [...updatedMessages],
                };
              } else {
                // If message doesn't exist yet, add it as new
                return {
                  ...prev,
                  messages: [...prev.messages, newMessage],
                };
              }
            });
          }
          break;

        case RESPONSE_TYPES.KPI_DASHBOARD_UPDATE:
          if (event.data) {
            onKpiDashboardUpdate?.(event.data);
          }
          break;

        default:
          logger.warn(
            "[ChatJess Hook] Tipo de evento desconhecido:",
            event.type,
          );
      }
    },
    [
      onNewMessage,
      onFeedbackRequest,
      onError,
      onChatHistoryReceived,
      onKpiDashboardUpdate,
      generateUniqueId,
    ],
  );

  // Handler para mudanças de conexão
  const handleConnectionChange = useCallback((connected: boolean) => {
    setState((prev) => ({
      ...prev,
      connection: {
        ...prev.connection,
        isConnected: connected,
        isConnecting: false,
        error: connected ? null : prev.connection.error,
        lastPing: connected ? new Date() : prev.connection.lastPing,
      },
    }));

    if (connected) {
      reconnectAttemptsRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    } else {
      // Tentar reconectar se não foi desconexão manual
      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        const delay =
          reconnectDelay * Math.pow(2, reconnectAttemptsRef.current);

        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current++;
          connect();
        }, delay);
      }
    }
  }, []);

  // Handler para erros do WebSocket
  const handleWebSocketError = useCallback(
    (error: Error) => {
      logger.error("[ChatJess Hook] Erro no WebSocket:", error);
      onError?.(error);
      setState((prev) => ({
        ...prev,
        connection: {
          ...prev.connection,
          error: error.message,
        },
      }));
    },
    [onError],
  );

  // Função para conectar
  const connect = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    setState((prev) => ({
      ...prev,
      connection: {
        ...prev.connection,
        isConnecting: true,
        error: null,
      },
    }));

    try {
      socketRef.current = connectToChatJessWebSocket(
        handleWebSocketMessage,
        handleConnectionChange,
        handleWebSocketError,
      );
    } catch (error) {
      logger.error("[ChatJess Hook] Erro ao conectar:", error);
      handleWebSocketError(error as Error);
    }
  }, [handleWebSocketMessage, handleConnectionChange, handleWebSocketError]);

  // Função para desconectar
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    reconnectAttemptsRef.current = maxReconnectAttempts; // Impedir reconexão automática

    // Call module-level disconnect to ensure singleton cleanup
    disconnectChatJessWebSocket();

    // Also clear local ref
    if (socketRef.current) {
      socketRef.current = null;
    }

    setState((prev) => ({
      ...prev,
      connection: {
        isConnected: false,
        isConnecting: false,
        error: null,
      },
      isTyping: false,
    }));
  }, []);

  // Função para reconectar manualmente
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    disconnect();
    setTimeout(connect, 500);
  }, [connect, disconnect]);

  // Helper function to get user's clientID from auth data
  const getUserClientID = useCallback((): string => {
    // First, try to use the stored clientID from when chatConnected was sent
    if (connectedClientIDRef.current) {
      return connectedClientIDRef.current;
    }

    // Fallback to parsing auth data from localStorage
    try {
      const authData = localStorage.getItem("authUser");
      if (authData) {
        const parsedAuth = JSON.parse(authData);
        const telephone = parsedAuth.telephone || "";
        const clientID = telephone ? `${telephone}@c.us` : "";
        return clientID;
      }
    } catch (error) {
      logger.error("💥 [ChatJess Hook] Error parsing auth data:", error);
    }
    return "";
  }, []);

  // Helper function to transform ChatJessMessage to WebSocketMessage
  const transformToWebSocketMessage = useCallback(
    (message: ChatJessMessage): WebSocketMessage => {
      const clientID = getUserClientID();
      const now = Date.now();

      // Map file attachments to Media object (Go expects single media, not array)
      const attachmentsWithUrls =
        message.attachments?.filter((att) => att.url) || [];

      const webSocketMessage: WebSocketMessage = {
        id: message.id,
        clientID: clientID, // Note: camelCase to match Go json tag
        timestamp: Math.floor(now / 1000), // Unix timestamp in seconds
        body: message.content,
        from: clientID,
        fromMe: true, // User messages are always from the user
        hasMedia: attachmentsWithUrls.length > 0,
        media:
          attachmentsWithUrls.length > 0
            ? {
                url: attachmentsWithUrls[0].url!, // For now, just use the first media item
                mimetype: attachmentsWithUrls[0].type,
                filename: attachmentsWithUrls[0].name,
                filesize: attachmentsWithUrls[0].size,
              }
            : undefined,
        notifyName: message.userName || "",
        channel: "web", // Default channel for web messages
        session: "", // Will be set by server
        shouldReply: true, // User messages should get replies
      };

      return webSocketMessage;
    },
    [getUserClientID],
  );

  // Função para enviar mensagem
  const sendMessage = useCallback(
    (message: ChatJessMessage) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        // Transform ChatJessMessage to WebSocketMessage
        const webSocketMessage = transformToWebSocketMessage(message);

        // Create AgentMessage with teamMessage requestType
        const agentMessage: TeamMessage = {
          requestType: REQUEST_TYPES.TEAM_MESSAGE,
          createdAt: new Date().toISOString(),
          payload: webSocketMessage,
        };

        const jsonPayload = JSON.stringify(agentMessage);

        socketRef.current.send(jsonPayload);

        // Adicionar mensagem localmente e definir isWaitingForResponse, avoiding duplicates
        setState((prev) => {
          // Check if message with this ID already exists
          const existingMessage = prev.messages.find(
            (msg) => msg.id === message.id,
          );

          if (existingMessage) {
            // Generate new ID to avoid duplicate keys
            message.id = generateUniqueId(message.id, "local");
          }

          return {
            ...prev,
            messages: [...prev.messages, message],
            isWaitingForResponse: true,
          };
        });
      }
    },
    [transformToWebSocketMessage, generateUniqueId],
  );

  // Função para enviar resposta de feedback
  const sendFeedbackResponse = useCallback(
    (
      feedbackId: string,
      response: string,
      requestMessage?: ChatJessMessage,
      attachments?: FileAttachment[],
      questionIndex: number = 0,
    ) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        if (!requestMessage) {
          return;
        }

        // Build HumanSupportMessage array from original team_messages plus new answer
        const humanSupportMessages: HumanSupportMessage[] = [];

        if (
          requestMessage.originalTeamMessages &&
          requestMessage.originalTeamMessages.length > 0
        ) {
          // Process original team_messages to build the complete conversation flow
          // Sort by timestamp to ensure correct order
          const sortedMessages = [...requestMessage.originalTeamMessages].sort(
            (a, b) => a.timestamp - b.timestamp,
          );

          for (const teamMsg of sortedMessages) {
            if (teamMsg.who === "assistant") {
              // Assistant message - this is a question
              humanSupportMessages.push({
                question: teamMsg.body,
                answer: "", // Will be filled by next user message or the new response
              });
            } else if (teamMsg.who === "user") {
              // User message - this is an answer to the previous question
              if (humanSupportMessages.length > 0) {
                const lastIndex = humanSupportMessages.length - 1;
                if (humanSupportMessages[lastIndex].answer === "") {
                  humanSupportMessages[lastIndex].answer = teamMsg.body;
                }
              }
            }
          }

          // Set the new answer at the submitted question index (with bounds check)
          const targetIndex =
            questionIndex >= 0 && questionIndex < humanSupportMessages.length
              ? questionIndex
              : humanSupportMessages.findIndex((msg) => msg.answer === "");
          const safeIndex =
            targetIndex >= 0 ? targetIndex : humanSupportMessages.length - 1;
          if (safeIndex >= 0 && safeIndex < humanSupportMessages.length) {
            humanSupportMessages[safeIndex].answer = response;
          }
        } else if (
          requestMessage.supportMessages &&
          requestMessage.supportMessages.length > 0
        ) {
          // Fallback: use processed supportMessages (preserve existing media/answerMedia)
          requestMessage.supportMessages.forEach((supportMsg, index) => {
            humanSupportMessages.push({
              question: supportMsg.question,
              answer:
                index === questionIndex
                  ? response
                  : (supportMsg.answer ?? ""),
              media: supportMsg.media,
              answerMedia:
                index === questionIndex ? undefined : supportMsg.answerMedia,
            });
          });
          // Set answerMedia for the current submission at questionIndex
          if (
            questionIndex >= 0 &&
            questionIndex < humanSupportMessages.length &&
            attachments?.length
          ) {
            const mapped = attachments
              .filter((a) => a.url)
              .map((a) => ({
                url: a.url!,
                mimetype: a.type,
                filename: a.name,
                filesize: a.size,
              }));
            if (mapped.length > 0) {
              humanSupportMessages[questionIndex].answerMedia = mapped;
            }
          }
        } else {
          // Final fallback: create a single question/answer pair from the message content
          const single: HumanSupportMessage = {
            question: requestMessage.content,
            answer: response,
          };
          if (attachments?.length) {
            const mapped = attachments
              .filter((a) => a.url)
              .map((a) => ({
                url: a.url!,
                mimetype: a.type,
                filename: a.name,
                filesize: a.size,
              }));
            if (mapped.length > 0) single.answerMedia = mapped;
          }
          humanSupportMessages.push(single);
        }

        // Set answerMedia (all attachments) on the target message when built from originalTeamMessages
        if (
          requestMessage.originalTeamMessages?.length &&
          attachments?.length
        ) {
          const targetIdx =
            questionIndex >= 0 && questionIndex < humanSupportMessages.length
              ? questionIndex
              : humanSupportMessages.findIndex((msg) => msg.answer === "");
          const idx =
            targetIdx >= 0 ? targetIdx : humanSupportMessages.length - 1;
          if (idx >= 0 && idx < humanSupportMessages.length) {
            const mapped = attachments
              .filter((a) => a.url)
              .map((a) => ({
                url: a.url!,
                mimetype: a.type,
                filename: a.name,
                filesize: a.size,
              }));
            if (mapped.length > 0) {
              humanSupportMessages[idx].answerMedia = mapped;
            }
          }
        }

        // Create HumanSupport object
        const humanSupport: HumanSupport = {
          isRequired: true, // Always true for human requests
          isCompleted: requestMessage.isCompleted || false,
          humanSupportType:
            requestMessage.supportType || "requiresHumanApproval",
          humanSupportMessage: humanSupportMessages,
          humanQaId: feedbackId,
          createdAt: requestMessage.timestamp.toISOString(),
        };

        // Get clientId using the same method as sendMessage
        const clientId = getUserClientID();

        // Create HumanSupportDTO payload
        const payload: HumanSupportDTO = {
          humanSupport,
          messageId: requestMessage.id, // Use the original message ID
          stepId: requestMessage.stepId || "", // Use stepId from request message
          clientId: clientId, // ID of the client that this request is for
        };

        // Create AgentMessage with submitHumanQAAnswer requestType
        const agentMessage: HumanQAAnswerMessage = {
          requestType: REQUEST_TYPES.SUBMIT_HUMAN_QA_ANSWER,
          createdAt: new Date().toISOString(),
          payload,
        };

        const jsonPayload = JSON.stringify(agentMessage);

        socketRef.current.send(jsonPayload);
      }
    },
    [],
  );

  const sendTaskFeedback = useCallback(
    (task: Task, stepId: string, feedback: string) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const clientId = getUserClientID();

        const supportType: HumanSupportType = getSupportType(
          task.eventType || WorkflowEventType.PausedDueApprovalRequired,
        );

        let question = "";
        if (task.steps?.length) {
          for (let i = task.steps.length - 1; i >= 0; i--) {
            if (
              task.steps[i].humanSupport?.[0]?.humanSupportMessage?.[0]
                ?.question
            ) {
              question =
                task.steps[i].humanSupport?.[0]?.humanSupportMessage?.[0]
                  ?.question || "";
              break;
            }
          }
        }

        const humanSupport: TaskHumanSupport = {
          stepId,
          isRequired: true,
          isCompleted: false,
          humanSupportType: supportType,
          humanSupportMessage: [
            {
              question,
              answer: feedback,
            },
          ],
          humanQaId: task.humanQaId,
          createdAt: new Date().toISOString(),
        };

        const payload = {
          humanSupport,
          taskId: task.id,
          stepId,
          clientId,
        };

        const agentMessage = {
          requestType: REQUEST_TYPES.SUBMIT_HUMAN_QA_ANSWER,
          createdAt: new Date().toISOString(),
          payload,
        };

        const jsonPayload = JSON.stringify(agentMessage);

        socketRef.current.send(jsonPayload);
      }
    },
    [],
  );

  const getSupportType = (eventType: WorkflowEventType): HumanSupportType => {
    if (eventType === WorkflowEventType.PausedDueApprovalRequired) {
      return "requiresHumanApproval";
    }

    if (eventType === WorkflowEventType.PausedDueHumanSupportRequired) {
      return "requiresHumanAnswer";
    }

    if (
      eventType === WorkflowEventType.PausedDueMissingInput ||
      eventType === WorkflowEventType.PausedDueMissingUserConfirmation ||
      eventType === WorkflowEventType.Paused
    ) {
      return "requiresHumanAction";
    }

    return "requiresHumanApproval";
  };

  // Função para enviar chatConnected request
  const sendChatConnected = useCallback(
    (clientID: string, clientType: "staff" | "user" = "staff") => {
      // Store the clientID for use in sendMessage
      connectedClientIDRef.current = clientID;

      if (socketRef.current?.readyState === WebSocket.OPEN) {
        // Set waiting flag
        waitingForHistoryRef.current = true;

        const payload: ChatConnectedPayload = {
          clientID,
          clientType,
        };

        const agentMessage: ChatConnectedMessage = {
          requestType: REQUEST_TYPES.CHAT_CONNECTED,
          createdAt: new Date().toISOString(),
          payload,
        };

        socketRef.current.send(JSON.stringify(agentMessage));

        // Notify that chatConnected was sent
        onChatConnectedSent?.();
      }
    },
    [onChatConnectedSent],
  );

  // Efeito para conectar na montagem
  useEffect(() => {
    connect();

    // Cleanup na desmontagem
    return () => {
      disconnect();
    };
  }, []);

  return {
    // Estado
    messages: state.messages,
    isTyping: state.isTyping,
    connection: state.connection,
    isWaitingForResponse: state.isWaitingForResponse,

    // Ações
    sendMessage,
    sendFeedbackResponse,
    sendTaskFeedback,
    sendChatConnected,
    reconnect,
    disconnect,

    // Utilitários
    isConnected: state.connection.isConnected,
  };
};
